<template>
  <div class="min-h-screen bg-gray-50">
    <div class="p-6 space-y-6">
      <div class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 class="text-2xl font-semibold text-gray-900">사용자 관리</h1>
          <p class="text-sm text-gray-500">
            계정 상태, 권한, 로그인 이력을 한눈에 관리하세요.
          </p>
        </div>
        <div class="flex flex-wrap gap-2">
          <button @click="openAddUserModal"
            class="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700">
            <i class="fas fa-user-plus text-sm"></i>
            사용자 추가
          </button>
        </div>
      </div>

      <div class="grid gap-4 lg:grid-cols-4">
        <div class="rounded-xl border border-gray-100 bg-white p-3 shadow-sm lg:col-span-1">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-xs font-semibold uppercase text-gray-500">전체 사용자</p>
              <p class="text-3xl font-semibold text-gray-900">{{ users.length }}</p>
            </div>
            <div class="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-blue-600">
              <i class="fas fa-users"></i>
            </div>
          </div>
          <div class="mt-4 space-y-3">
            <div class="flex items-center justify-between text-sm text-gray-600">
              <span>활성</span>
              <span class="font-semibold text-gray-900">{{ activeCount }}</span>
            </div>
            <div class="flex items-center justify-between text-sm text-gray-600">
              <span>비활성/차단</span>
              <span class="font-semibold text-gray-900">{{ blockedCount }}</span>
            </div>
          </div>
        </div>

        <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm lg:col-span-2">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-xs font-semibold uppercase text-gray-500">가입 추이 (최근 12개월)</p>
              <p class="text-sm text-gray-500">월별 신규 가입 수</p>
            </div>
            <div class="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-50 text-indigo-600">
              <i class="fas fa-chart-bar"></i>
            </div>
          </div>
          <div class="mt-4 grid grid-cols-6 gap-2 sm:grid-cols-8 lg:grid-cols-12">
            <div v-for="item in monthlySeries" :key="item.label" class="flex flex-col items-center gap-1">
              <div class="flex h-20 w-full items-end justify-center rounded bg-gray-50">
                <div class="w-full max-w-[18px] rounded-t bg-indigo-500 transition"
                  :style="{ height: `${item.percent}%` }"></div>
              </div>
              <p class="text-[10px] font-medium text-gray-500">{{ item.label }}</p>
              <p class="text-xs font-semibold text-gray-800">{{ item.count }}</p>
            </div>
          </div>
        </div>

        <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-1 lg:col-span-1">
          <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-xs font-semibold uppercase text-gray-500">직무 분포</p>
                <p class="text-sm text-gray-500">매니저/브로커/트레이더</p>
              </div>
              <div class="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                <i class="fas fa-chart-pie"></i>
              </div>
            </div>
            <div class="mt-4 flex items-center gap-4">
              <div class="h-24 w-24 rounded-full" :style="{
                background: `conic-gradient(#2563eb 0% ${roleStats.managerPct}%, #10b981 ${roleStats.managerPct}% ${roleStats.managerPct + roleStats.brokerPct}%, #f59e0b ${roleStats.managerPct + roleStats.brokerPct}% 100%)`,
              }"></div>
              <div class="space-y-2 text-sm">
                <div class="flex items-center gap-2">
                  <span class="h-2 w-2 rounded-full bg-blue-600"></span>
                  <span class="text-gray-700">매니저</span>
                  <span class="font-semibold text-gray-900">{{ managerCount }}</span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="h-2 w-2 rounded-full bg-emerald-500"></span>
                  <span class="text-gray-700">브로커</span>
                  <span class="font-semibold text-gray-900">{{ brokerCount }}</span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="h-2 w-2 rounded-full bg-amber-500"></span>
                  <span class="text-gray-700">트레이더</span>
                  <span class="font-semibold text-gray-900">{{ traderCount }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="rounded-xl border border-gray-100 bg-white shadow-sm transition">
        <div class="flex flex-col gap-3 border-b border-gray-100 p-4 lg:flex-row lg:items-center lg:justify-between">
          <div class="flex flex-1 flex-col gap-3 lg:flex-row lg:items-center">
            <div class="relative flex-1 min-w-[220px]">
              <i class="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input :value="searchQuery" @input="onSearchInput" @compositionstart="isComposing = true"
                @compositionend="onCompositionEnd" type="text" placeholder="이름, 설명으로 검색"
                class="w-full rounded-lg border border-gray-200 bg-gray-50 px-10 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:border-blue-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-100" />
            </div>
            <div class="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center">
              <select v-model="statusFilter"
                class="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-100 sm:w-40">
                <option value="ALL">상태 전체</option>
                <option value="ACTIVE">활성</option>
                <option value="BLOCKED">비활성</option>
              </select>
              <select v-model="typeFilter"
                class="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-100 sm:w-40">
                <option value="ALL">직무 전체</option>
                <option value="MANAGER">매니저</option>
                <option value="BROKER">브로커</option>
                <option value="TRADER">트레이더</option>
              </select>
            </div>
          </div>
          <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
            <button @click="toggleSort('lastLoginDateTime')"
              class="flex items-center justify-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:border-blue-200 hover:text-blue-600">
              <i class="fas fa-clock text-xs"></i>
              최근 로그인
              <span class="text-xs text-gray-500">{{
                getSortLabel('lastLoginDateTime')
                }}</span>
            </button>
            <button @click="toggleSort('name')"
              class="flex items-center justify-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:border-blue-200 hover:text-blue-600">
              <i class="fas fa-sort-alpha-down text-xs"></i>
              이름
              <span class="text-xs text-gray-500">{{
                getSortLabel('name')
                }}</span>
            </button>
            <button @click="toggleSort('createdDateTime')"
              class="flex items-center justify-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:border-blue-200 hover:text-blue-600">
              <i class="fas fa-calendar-plus text-xs"></i>
              가입일
              <span class="text-xs text-gray-500">{{
                getSortLabel('createdDateTime')
                }}</span>
            </button>
          </div>
        </div>

        <div class="p-4">
          <div v-if="loading" class="flex min-h-[280px] items-center justify-center text-sm text-gray-500">
            <div class="flex items-center gap-2 rounded-full border px-3 py-2">
              <span class="h-2 w-2 animate-ping rounded-full bg-blue-500"></span>
              불러오는 중입니다...
            </div>
          </div>

          <template v-else>
            <div v-if="displayUsers.length" class="overflow-hidden">
              <div class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-100">
                  <thead class="bg-gray-50 text-xs font-semibold text-gray-500">
                    <tr>
                      <th class="px-4 py-3 text-left">사용자</th>
                      <th class="px-4 py-3 text-left">설명</th>
                      <th class="px-4 py-3 text-left">직무</th>
                      <th class="px-4 py-3 text-left">상태</th>
                      <th class="px-4 py-3 text-left">가입일</th>
                      <th class="px-4 py-3 text-left">최근 로그인</th>
                      <th class="px-4 py-3 text-left">작업</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-100 bg-white text-sm">
                    <tr v-for="user in displayUsers" :key="user.id" class="transition hover:bg-blue-50/40">
                      <td class="px-4 py-3">
                        <div class="flex items-center gap-3">
                          <div class="flex h-9 w-9 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                            <i class="fas fa-user text-sm"></i>
                          </div>
                          <div>
                            <p class="font-medium text-gray-900">
                              {{ user.name }}
                            </p>
                            <p class="text-xs text-gray-500">
                              ID: {{ user.id }}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td class="px-4 py-3 text-gray-700">
                        {{ user.description || '설명 없음' }}
                      </td>
                      <td class="px-4 py-3">
                        <span
                          :class="['inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium', getUserTypeClass(user.type).wrapper]">
                          <span :class="['h-2 w-2 rounded-full', getUserTypeClass(user.type).dot]"></span>
                          {{ getUserTypeName(user.type) }}
                        </span>
                      </td>
                      <td class="px-4 py-3">
                        <span :class="[
                          'inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold',
                          user.status === 'ACTIVE'
                            ? 'bg-green-50 text-green-700 ring-1 ring-green-100'
                            : 'bg-rose-50 text-rose-700 ring-1 ring-rose-100',
                        ]">
                          <span :class="[
                            'h-2 w-2 rounded-full',
                            user.status === 'ACTIVE' ? 'bg-green-500' : 'bg-rose-500',
                          ]"></span>
                          {{ user.status === 'ACTIVE' ? '활성' : '비활성' }}
                        </span>
                      </td>
                      <td class="px-4 py-3 text-gray-700">
                        {{ formatDate(user.createdDateTime) }}
                      </td>
                      <td class="px-4 py-3 text-gray-700">
                        {{ formatDateTime(user.lastLoginDateTime) }}
                      </td>
                      <td class="px-4 py-3">
                        <div class="flex flex-wrap gap-2">
                          <button @click="openEditModal(user)"
                            class="rounded-lg bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 transition hover:bg-blue-100">
                            수정
                          </button>
                          <button @click="openResetPasswordModal(user)"
                            class="rounded-lg bg-gray-100 px-3 py-2 text-xs font-semibold text-gray-700 transition hover:bg-gray-200">
                            비밀번호 재설정
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div v-else
              class="flex min-h-[240px] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-gray-200 bg-gray-50 text-gray-500">
              <i class="fas fa-inbox text-2xl text-gray-400"></i>
              <p class="text-sm font-medium">조건에 맞는 사용자가 없습니다.</p>
              <p class="text-xs text-gray-400">
                검색어나 필터를 조정해 다시 시도해 주세요.
              </p>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Edit User Modal -->
    <div v-if="showEditModal" class="fixed inset-0 z-20 flex items-center justify-center bg-black/50 px-4">
      <div class="w-full max-w-lg rounded-xl bg-white shadow-xl">
        <div class="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <div>
            <p class="text-xs font-semibold uppercase text-blue-600">
              사용자 편집
            </p>
            <h3 class="text-lg font-semibold text-gray-900">사용자 정보 수정</h3>
          </div>
          <button @click="closeEditModal" class="text-gray-400 transition hover:text-gray-600">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <form @submit.prevent="handleEditSubmit" class="space-y-4 px-6 py-5">
          <div>
            <label class="mb-1 block text-sm font-medium text-gray-700">
              사용자명
            </label>
            <input :value="editingUser?.name" disabled
              class="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700" />
          </div>
          <div>
            <label class="mb-1 block text-sm font-medium text-gray-700">
              설명
            </label>
            <input v-model="editingUser.description" type="text"
              class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-100" />
          </div>
          <div class="grid gap-3 sm:grid-cols-2">
            <div>
              <label class="mb-1 block text-sm font-medium text-gray-700">
                직무
              </label>
              <select v-model="editingUser.type"
                class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-100">
                <option value="BROKER">브로커</option>
                <option value="MANAGER">매니저</option>
                <option value="TRADER">트레이더</option>
              </select>
            </div>
            <div>
              <label class="mb-1 block text-sm font-medium text-gray-700">
                상태
              </label>
              <select v-model="editingUser.status"
                class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-100">
                <option value="ACTIVE">활성</option>
                <option value="BLOCKED">비활성</option>
              </select>
            </div>
          </div>
          <div class="flex justify-end gap-2 pt-2">
            <button type="button" @click="closeEditModal"
              class="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100">
              취소
            </button>
            <button type="submit" :disabled="actionLoading"
              class="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300">
              {{ actionLoading ? '저장 중...' : '저장' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Reset Password Modal -->
    <div v-if="showResetPasswordModal" class="fixed inset-0 z-20 flex items-center justify-center bg-black/50 px-4">
      <div class="w-full max-w-lg rounded-xl bg-white shadow-xl">
        <div class="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <div>
            <p class="text-xs font-semibold uppercase text-amber-600">
              보안 작업
            </p>
            <h3 class="text-lg font-semibold text-gray-900">비밀번호 재설정</h3>
          </div>
          <button @click="closeResetPasswordModal" class="text-gray-400 transition hover:text-gray-600">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <form @submit.prevent="handleResetPassword" class="space-y-4 px-6 py-5">
          <div>
            <label class="mb-1 block text-sm font-medium text-gray-700">
              새 비밀번호
            </label>
            <input v-model="newPassword" type="password" required
              class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-100" />
          </div>
          <div>
            <label class="mb-1 block text-sm font-medium text-gray-700">
              비밀번호 확인
            </label>
            <input v-model="confirmPassword" type="password" required
              class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-100" />
          </div>
          <p class="text-xs text-gray-500">
            입력한 비밀번호를 다시 확인해 주세요. 변경 시 즉시 적용됩니다.
          </p>
          <div class="flex justify-end gap-2 pt-2">
            <button type="button" @click="closeResetPasswordModal"
              class="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100">
              취소
            </button>
            <button type="submit" :disabled="actionLoading"
              class="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300">
              {{ actionLoading ? '변경 중...' : '변경' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Add User Modal -->
    <div v-if="showAddUserModal" class="fixed inset-0 z-20 flex items-center justify-center bg-black/50 px-4">
      <div class="w-full max-w-lg rounded-xl bg-white shadow-xl">
        <div class="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <div>
            <p class="text-xs font-semibold uppercase text-green-600">
              새 사용자
            </p>
            <h3 class="text-lg font-semibold text-gray-900">사용자 추가</h3>
          </div>
          <button @click="closeAddUserModal" class="text-gray-400 transition hover:text-gray-600">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <form @submit.prevent="handleAddUser" class="space-y-4 px-6 py-5">
          <div>
            <label class="mb-1 block text-sm font-medium text-gray-700">
              사용자명
            </label>
            <input v-model="newUser.username" type="text" required
              class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-100" />
          </div>
          <div>
            <label class="mb-1 block text-sm font-medium text-gray-700">
              설명
            </label>
            <input v-model="newUser.description" type="text"
              class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-100" />
          </div>
          <div class="grid gap-3 sm:grid-cols-2">
            <div>
              <label class="mb-1 block text-sm font-medium text-gray-700">
                직무
              </label>
              <select v-model="newUser.userType"
                class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-100">
                <option value="MANAGER">매니저</option>
                <option value="BROKER">브로커</option>
                <option value="TRADER">트레이더</option>
              </select>
            </div>
            <div>
              <label class="mb-1 block text-sm font-medium text-gray-700">
                비밀번호
              </label>
              <input v-model="newUser.password" type="password" required
                class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-100" />
            </div>
          </div>
          <div class="flex justify-end gap-2 pt-2">
            <button type="button" @click="closeAddUserModal"
              class="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100">
              취소
            </button>
            <button type="submit" :disabled="actionLoading"
              class="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300">
              {{ actionLoading ? '추가 중...' : '추가' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue';
import userService from '@/service/userService';

export default {
  setup() {
    const users = ref([]);
    const loading = ref(false);
    const error = ref(null);

    const showEditModal = ref(false);
    const showResetPasswordModal = ref(false);
    const showAddUserModal = ref(false);

    const editingUser = ref(null);
    const selectedUser = ref(null);

    const newPassword = ref('');
    const confirmPassword = ref('');

    const newUser = ref({
      username: '',
      description: '',
      userType: 'MANAGER',
      password: '',
    });

    const searchQuery = ref('');
    const isComposing = ref(false);
    const statusFilter = ref('ALL');
    const typeFilter = ref('ALL');
    const sortKey = ref('id');
    const sortDirection = ref('desc');

    const actionLoading = ref(false);

    const activeCount = computed(
      () => users.value.filter(user => user.status === 'ACTIVE').length
    );
    const blockedCount = computed(
      () => users.value.filter(user => user.status === 'BLOCKED').length
    );
    const managerCount = computed(
      () => users.value.filter(user => user.type === 'MANAGER').length
    );
    const brokerCount = computed(
      () => users.value.filter(user => user.type === 'BROKER').length
    );
    const traderCount = computed(
      () => users.value.filter(user => user.type === 'TRADER').length
    );

    const roleStats = computed(() => {
      const total = users.value.length || 1;
      const manager = managerCount.value;
      const broker = brokerCount.value;
      const trader = traderCount.value;
      return {
        managerPct: (manager / total) * 100,
        brokerPct: (broker / total) * 100,
        traderPct: (trader / total) * 100,
      };
    });

    const monthlySeries = computed(() => {
      const now = new Date();
      const months = [];
      for (let i = 11; i >= 0; i -= 1) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        months.push({
          key,
          label: `${String(d.getFullYear()).slice(2)}.${String(d.getMonth() + 1).padStart(2, '0')}`,
          count: 0,
        });
      }

      const map = months.reduce((acc, m) => {
        acc[m.key] = m;
        return acc;
      }, {});

      users.value.forEach(user => {
        if (!user.createdDateTime) return;
        const d = new Date(user.createdDateTime);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        if (map[key]) {
          map[key].count += 1;
        }
      });

      const maxCount = Math.max(...months.map(m => m.count), 1);
      return months.map(m => ({
        ...m,
        percent: (m.count / maxCount) * 100,
      }));
    });

    const getUserList = async () => {
      try {
        loading.value = true;
        error.value = null;
        const data = await userService.getUserList();
        users.value = data;
      } catch (err) {
        error.value = '사용자 목록을 불러오는 중 오류가 발생했습니다.';
        console.error('Error fetching users:', err);
      } finally {
        loading.value = false;
      }
    };

    const toggleSort = key => {
      if (sortKey.value === key) {
        sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
      } else {
        sortKey.value = key;
        sortDirection.value = 'desc';
      }
    };

    const getSortLabel = key => {
      if (sortKey.value !== key) return '정렬';
      return sortDirection.value === 'asc' ? '오름차순' : '내림차순';
    };

    const displayUsers = computed(() => {
      const query = searchQuery.value.trim().toLowerCase();
      const filtered = users.value.filter(user => {
        const matchesQuery =
          !query ||
          [user.name, user.description]
            .filter(Boolean)
            .some(field => field.toLowerCase().includes(query));
        const matchesStatus =
          statusFilter.value === 'ALL' || user.status === statusFilter.value;
        const matchesType =
          typeFilter.value === 'ALL' || user.type === typeFilter.value;
        return matchesQuery && matchesStatus && matchesType;
      });

      const getComparable = user => {
        if (sortKey.value === 'lastLoginDateTime') {
          return user.lastLoginDateTime
            ? new Date(user.lastLoginDateTime).getTime()
            : 0;
        }
        if (sortKey.value === 'createdDateTime') {
          return user.createdDateTime
            ? new Date(user.createdDateTime).getTime()
            : 0;
        }
        if (sortKey.value === 'id') {
          return Number(user.id) || 0;
        }
        if (sortKey.value === 'name') {
          return (user.name || '').toLowerCase();
        }
        return (user[sortKey.value] || '').toString().toLowerCase();
      };

      const direction = sortDirection.value === 'asc' ? 1 : -1;
      return [...filtered].sort((a, b) => {
        const aVal = getComparable(a);
        const bVal = getComparable(b);
        if (aVal === bVal) return 0;
        return aVal > bVal ? direction : -direction;
      });
    });

    const openEditModal = user => {
      editingUser.value = { ...user };
      showEditModal.value = true;
    };

    const closeEditModal = () => {
      showEditModal.value = false;
      editingUser.value = null;
    };

    const handleEditSubmit = async () => {
      if (!editingUser.value || !editingUser.value.id) {
        alert('사용자 정보가 올바르지 않습니다.');
        return;
      }
      try {
        actionLoading.value = true;
        await userService.updateUser(editingUser.value.id, {
          description: editingUser.value.description,
          type: editingUser.value.type,
          status: editingUser.value.status,
        });
        await getUserList();
        closeEditModal();
      } catch (err) {
        console.error('Error updating user:', err);
        alert('사용자 정보 수정 중 오류가 발생했습니다.');
      } finally {
        actionLoading.value = false;
      }
    };

    const openResetPasswordModal = user => {
      selectedUser.value = user;
      showResetPasswordModal.value = true;
      newPassword.value = '';
      confirmPassword.value = '';
    };

    const closeResetPasswordModal = () => {
      showResetPasswordModal.value = false;
      selectedUser.value = null;
      newPassword.value = '';
      confirmPassword.value = '';
    };

    const handleResetPassword = async () => {
      if (newPassword.value !== confirmPassword.value) {
        alert('비밀번호가 일치하지 않습니다.');
        return;
      }

      if (newPassword.value.length < 4) {
        alert('비밀번호는 최소 4자리 이상이어야 합니다.');
        return;
      }

      try {
        actionLoading.value = true;
        await userService.resetPassword(
          selectedUser.value.id,
          newPassword.value
        );
        closeResetPasswordModal();
        alert('비밀번호가 변경되었습니다.');
      } catch (err) {
        console.error('Error resetting password:', err);
        alert('비밀번호 변경 중 오류가 발생했습니다.');
      } finally {
        actionLoading.value = false;
      }
    };

    const getUserTypeName = type => {
      const types = {
        MANAGER: '매니저',
        BROKER: '브로커',
        TRADER: '트레이더',
      };
      return types[type] || type;
    };

    const getUserTypeClass = type => {
      const mapping = {
        MANAGER: {
          wrapper: 'bg-blue-50 text-blue-700',
          dot: 'bg-blue-400',
        },
        BROKER: {
          wrapper: 'bg-emerald-50 text-emerald-700',
          dot: 'bg-emerald-400',
        },
        TRADER: {
          wrapper: 'bg-amber-50 text-amber-700',
          dot: 'bg-amber-400',
        },
      };
      return mapping[type] || { wrapper: 'bg-gray-100 text-gray-700', dot: 'bg-gray-400' };
    };

    const onSearchInput = event => {
      if (isComposing.value) return;
      searchQuery.value = event.target.value;
    };

    const onCompositionEnd = event => {
      isComposing.value = false;
      searchQuery.value = event.target.value;
    };

    const formatDate = dateTime => {
      if (!dateTime) return '가입일 정보 없음';
      const date = new Date(dateTime);
      return new Intl.DateTimeFormat('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(date);
    };

    const formatDateTime = dateTime => {
      if (!dateTime) return '로그인 이력 없음';

      const date = new Date(dateTime);
      return new Intl.DateTimeFormat('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(date);
    };

    const openAddUserModal = () => {
      showAddUserModal.value = true;
      newUser.value = {
        username: '',
        description: '',
        userType: 'MANAGER',
        password: '',
      };
    };

    const closeAddUserModal = () => {
      showAddUserModal.value = false;
      newUser.value = {
        username: '',
        description: '',
        userType: 'MANAGER',
        password: '',
      };
    };

    const handleAddUser = async () => {
      try {
        actionLoading.value = true;
        await userService.registUser(newUser.value);
        await getUserList();
        closeAddUserModal();
      } catch (err) {
        console.error('Error creating user:', err);
        alert(err.message || '사용자 추가 중 오류가 발생했습니다.');
      } finally {
        actionLoading.value = false;
      }
    };

    onMounted(() => {
      getUserList();
    });

    return {
      users,
      loading,
      error,
      showEditModal,
      showResetPasswordModal,
      showAddUserModal,
      editingUser,
      selectedUser,
      newPassword,
      confirmPassword,
      newUser,
      searchQuery,
      statusFilter,
      typeFilter,
      sortKey,
      sortDirection,
      isComposing,
      onSearchInput,
      onCompositionEnd,
      actionLoading,
      activeCount,
      blockedCount,
      managerCount,
      brokerCount,
      traderCount,
      roleStats,
      monthlySeries,
      displayUsers,
      getUserList,
      toggleSort,
      getSortLabel,
      openEditModal,
      closeEditModal,
      handleEditSubmit,
      openResetPasswordModal,
      closeResetPasswordModal,
      handleResetPassword,
      getUserTypeName,
      getUserTypeClass,
      formatDate,
      formatDateTime,
      openAddUserModal,
      closeAddUserModal,
      handleAddUser,
    };
  },
};
</script>
